// utils/phoneUaDirective.ts — реалізація директиви v-phone-ua (маска "+380 XX XXX XX XX").
// Чистий Vue-модуль без Nuxt-АПІ: реєструється у plugins/phone-ua.ts, тестується напряму.
// Поведінка вводу портована з еталонної реалізації pages/login.vue.
// Працює з v-model: після кожного переформатування диспатчиться синтетичний
// "input" (lock захищає від ре-ентрантності власного обробника).
import type { ObjectDirective } from "vue";
import {
  digits,
  formatUaPhone,
  uaLocalDigits,
  PHONE_PREFIX,
  PHONE_PLACEHOLDER,
} from "~/utils/phone";

const MAX_LEN = PHONE_PLACEHOLDER.length; // 17, "+380 XX XXX XX XX"

type PhoneInput = HTMLInputElement & {
  __pua?: { lock: boolean; cleanup: () => void };
};

function applyValue(el: PhoneInput, next: string, caretPos?: number) {
  if (el.value === next) return;
  const state = el.__pua!;
  state.lock = true;
  el.value = next;
  // каретку чіпаємо тільки коли поле у фокусі
  if (document.activeElement === el) {
    const pos = caretPos ?? next.length;
    try {
      el.setSelectionRange(pos, pos);
    } catch {}
  }
  el.dispatchEvent(new Event("input", { bubbles: true }));
  state.lock = false;
}

/** Позиція каретки одразу після need-ної цифри національного номера. */
function caretForDigits(next: string, need: number): number {
  let i = 0;
  let seen = 0;
  while (i < next.length && seen < need) {
    if (i >= PHONE_PREFIX.length && /\d/.test(next[i])) seen++;
    i++;
  }
  return Math.max(i, PHONE_PREFIX.length);
}

export const phoneUaDirective: ObjectDirective<PhoneInput> = {
  getSSRProps() {
    return {};
  },

  mounted(el) {
    el.setAttribute("inputmode", "tel");
    el.setAttribute("autocomplete", "tel");
    el.setAttribute("maxlength", String(MAX_LEN));

    el.__pua = { lock: false, cleanup: () => {} };

    // значення, що прийшло одразу (E.164 з БД тощо) — маскуємо;
    // порожнє / голий префікс не чіпаємо, щоб не ховати placeholder
    if (uaLocalDigits(el.value).length) applyValue(el, formatUaPhone(el.value));

    const onInput = () => {
      if (el.__pua!.lock) return;
      // зберігаємо позицію каретки: рахуємо цифри національного номера
      // до каретки у "сирому" значенні й повертаємось після тієї ж цифри
      const prev = el.value;
      const caret = el.selectionStart ?? prev.length;
      const lead = digits(prev).length - uaLocalDigits(prev).length;
      const need = Math.max(
        0,
        Math.min(digits(prev.slice(0, caret)).length - lead, 9),
      );
      const next = formatUaPhone(prev);
      applyValue(el, next, caretForDigits(next, need));
    };

    const onFocus = () => {
      if (!uaLocalDigits(el.value).length && el.value !== PHONE_PREFIX) {
        applyValue(el, PHONE_PREFIX);
      }
    };

    const onBlur = () => {
      // без жодної цифри повертаємо порожнє поле: placeholder і required знову працюють
      if (!uaLocalDigits(el.value).length && el.value !== "") {
        applyValue(el, "");
      }
    };

    const onPaste = (e: ClipboardEvent) => {
      e.preventDefault();
      const text = e.clipboardData?.getData("text") || "";
      applyValue(el, formatUaPhone(text));
    };

    const onKeydown = (e: KeyboardEvent) => {
      const start = el.selectionStart ?? 0;
      const end = el.selectionEnd ?? start;
      const min = PHONE_PREFIX.length;
      const noSelection = start === end;
      // не даємо редагувати зону префікса; із виділенням — можна,
      // onInput все одно переформатує результат
      if (noSelection && start < min && e.key.length === 1) {
        e.preventDefault();
        return;
      }
      if (
        noSelection &&
        ((e.key === "Backspace" && start <= min) ||
          (e.key === "Delete" && start < min))
      ) {
        e.preventDefault();
      }
    };

    el.addEventListener("input", onInput);
    el.addEventListener("focus", onFocus);
    el.addEventListener("blur", onBlur);
    el.addEventListener("paste", onPaste);
    el.addEventListener("keydown", onKeydown);

    el.__pua.cleanup = () => {
      el.removeEventListener("input", onInput);
      el.removeEventListener("focus", onFocus);
      el.removeEventListener("blur", onBlur);
      el.removeEventListener("paste", onPaste);
      el.removeEventListener("keydown", onKeydown);
    };
  },

  updated(el) {
    // значення, присвоєне програмно (async з БД) — теж маскуємо
    if (!el.__pua || el.__pua.lock) return;
    if (uaLocalDigits(el.value).length) {
      applyValue(el, formatUaPhone(el.value));
    }
  },

  unmounted(el) {
    el.__pua?.cleanup();
    delete el.__pua;
  },
};
